// src/components/games/FlipperGame.js
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { FaRedo, FaPlay } from 'react-icons/fa'; // Pour les boutons de contrôle
import './FlipperGame.css';

const TABLE_WIDTH = 300; // Largeur du plateau de jeu
const TABLE_HEIGHT = 500; // Hauteur du plateau de jeu
const BALL_RADIUS = 8;
const FLIPPER_WIDTH = 60;
const FLIPPER_HEIGHT = 10;
const FLIPPER_SPEED = 0.3; // Vitesse de rotation des flippers (radians par frame)
const FLIPPER_MAX_ANGLE = -Math.PI / 4; // Angle max pour le flipper gauche (vers le haut)
const GRAVITY = 0.2;
const PLUNGER_STRENGTH_MAX = 15;
const BUMPER_RADIUS = 15;

function FlipperGame() {
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [balls, setBalls] = useState(3);
  const [isGameOver, setIsGameOver] = useState(false);
  const [gameMessage, setGameMessage] = useState('Appuyez sur ESPACE pour Lancer');
  const [isPlaying, setIsPlaying] = useState(false);

  // États du jeu (utilisation de refs pour une mise à jour rapide dans la boucle d'animation)
  const ballRef = useRef({ x: TABLE_WIDTH - BALL_RADIUS - 15, y: TABLE_HEIGHT - 50, vx: 0, vy: 0, radius: BALL_RADIUS, launched: false });
  const leftFlipperRef = useRef({ x: TABLE_WIDTH / 2 - FLIPPER_WIDTH - 5, y: TABLE_HEIGHT - 60, width: FLIPPER_WIDTH, height: FLIPPER_HEIGHT, angle: 0, targetAngle: 0, active: false });
  const rightFlipperRef = useRef({ x: TABLE_WIDTH / 2 + 5, y: TABLE_HEIGHT - 60, width: FLIPPER_WIDTH, height: FLIPPER_HEIGHT, angle: 0, targetAngle: 0, active: false });
  const plungerRef = useRef({ x: TABLE_WIDTH - 25, y: TABLE_HEIGHT - 80, width: 15, height: 70, pullBack: 0, pulling: false });

  const bumpersRef = useRef([
    { x: TABLE_WIDTH / 2, y: 100, radius: BUMPER_RADIUS, points: 100, hit: false },
    { x: TABLE_WIDTH / 3, y: 180, radius: BUMPER_RADIUS, points: 50, hit: false },
    { x: (TABLE_WIDTH / 3) * 2, y: 180, radius: BUMPER_RADIUS, points: 50, hit: false },
  ]);

  const requestRef = useRef();


  // --- DESSIN ---
  const draw = useCallback((ctx) => {
    ctx.clearRect(0, 0, TABLE_WIDTH, TABLE_HEIGHT);

    // Fond de la table
    ctx.fillStyle = 'var(--flipper-table-bg, #2c3e50)';
    ctx.fillRect(0, 0, TABLE_WIDTH, TABLE_HEIGHT);
    
    // Couloir du lanceur
    ctx.fillStyle = 'var(--flipper-lane-bg, #34495e)';
    ctx.fillRect(TABLE_WIDTH - 30, 0, 30, TABLE_HEIGHT);

    // Dessiner les bumpers
    bumpersRef.current.forEach(bumper => {
      ctx.beginPath();
      ctx.arc(bumper.x, bumper.y, bumper.radius, 0, Math.PI * 2);
      ctx.fillStyle = bumper.hit ? 'var(--flipper-bumper-hit-color, #e74c3c)' : 'var(--flipper-bumper-color, #3498db)';
      ctx.fill();
      ctx.closePath();
      if (bumper.hit) bumper.hit = false; // Reset hit state after drawing
    });

    // Dessiner le lanceur
    ctx.fillStyle = 'var(--flipper-plunger-color, #95a5a6)';
    ctx.fillRect(plungerRef.current.x, plungerRef.current.y - plungerRef.current.pullBack, plungerRef.current.width, plungerRef.current.height);

    // Dessiner la bille
    if(ballRef.current.launched || plungerRef.current.pulling) {
        ctx.beginPath();
        ctx.arc(ballRef.current.x, ballRef.current.y, ballRef.current.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'var(--flipper-ball-color, #ecf0f1)';
        ctx.fill();
        ctx.closePath();
    }


    // Dessiner les flippers
    const drawFlipper = (flipper, isLeft) => {
      ctx.save();
      ctx.translate(flipper.x + (isLeft ? flipper.width : 0), flipper.y + flipper.height / 2);
      ctx.rotate(flipper.angle);
      ctx.fillStyle = flipper.active ? 'var(--flipper-active-color, #f1c40f)' : 'var(--flipper-idle-color, #bdc3c7)';
      ctx.fillRect(isLeft ? -flipper.width : 0, -flipper.height / 2, flipper.width, flipper.height);
      ctx.restore();
    };
    drawFlipper(leftFlipperRef.current, true);
    drawFlipper(rightFlipperRef.current, false);

  }, []); // Dépendances vides car utilise des refs pour l'état du jeu

  // --- LOGIQUE DU JEU ---
  const updateGame = useCallback(() => {
    if (!isPlaying || isGameOver) return;

    const ball = ballRef.current;
    const leftFlipper = leftFlipperRef.current;
    const rightFlipper = rightFlipperRef.current;
    const plunger = plungerRef.current;

    // Mouvement du lanceur (si la bille n'est pas lancée)
    if (!ball.launched && plunger.pulling) {
        // La bille suit le lanceur
        ball.y = plunger.y - plunger.pullBack - ball.radius;
        ball.x = plunger.x + plunger.width / 2;
    }


    // Mouvement de la bille (si lancée)
    if (ball.launched) {
      ball.vy += GRAVITY;
      ball.x += ball.vx;
      ball.y += ball.vy;

      // Collisions avec les murs
      if (ball.x - ball.radius < 0 || ball.x + ball.radius > TABLE_WIDTH) {
        ball.vx *= -0.8; // Rebond avec perte d'énergie
        ball.x = ball.x - ball.radius < 0 ? ball.radius : TABLE_WIDTH - ball.radius;
      }
      if (ball.y - ball.radius < 0) { // Plafond
        ball.vy *= -0.8;
        ball.y = ball.radius;
      }

      // Perte de la bille
      if (ball.y - ball.radius > TABLE_HEIGHT) {
        setBalls(b => b - 1);
        if (balls -1 <= 0) {
          setIsGameOver(true);
          setIsPlaying(false);
          setGameMessage('Game Over! ESPACE pour Rejouer');
        } else {
          // Réinitialiser la position de la bille pour un nouveau lancer
          ball.launched = false;
          ball.x = TABLE_WIDTH - BALL_RADIUS - 15;
          ball.y = TABLE_HEIGHT - 50;
          ball.vx = 0;
          ball.vy = 0;
          setGameMessage('Bille Perdue! ESPACE pour Lancer');
        }
      }

      // Collisions avec les bumpers
      bumpersRef.current.forEach(bumper => {
        const dist = Math.sqrt((ball.x - bumper.x)**2 + (ball.y - bumper.y)**2);
        if (dist < ball.radius + bumper.radius) {
          bumper.hit = true;
          setScore(s => s + bumper.points);
          // Rebond simple
          const angle = Math.atan2(ball.y - bumper.y, ball.x - bumper.x);
          const speedMagnitude = Math.sqrt(ball.vx**2 + ball.vy**2) * 1.1; // Léger boost
          ball.vx = Math.cos(angle) * speedMagnitude;
          ball.vy = Math.sin(angle) * speedMagnitude;
        }
      });
      
      // Collisions avec les flippers (très simplifié)
      [leftFlipper, rightFlipper].forEach((flipper, index) => {
        const isLeft = index === 0;
        // Point de pivot du flipper
        const pivotX = flipper.x + (isLeft ? flipper.width : 0);
        const pivotY = flipper.y + flipper.height / 2;
        
        // Position de l'extrémité du flipper
        const tipX = pivotX + (isLeft ? -flipper.width : flipper.width) * Math.cos(flipper.angle);
        const tipY = pivotY + (isLeft ? -flipper.width : flipper.width) * Math.sin(flipper.angle);

        // Collision simplifiée avec une ligne représentant le flipper
        // (Distance point à segment - une détection plus précise serait nécessaire pour un vrai flipper)
        const dx = tipX - pivotX;
        const dy = tipY - pivotY;
        const t = ((ball.x - pivotX) * dx + (ball.y - pivotY) * dy) / (dx*dx + dy*dy);
        let closestX, closestY;

        if (t < 0) { closestX = pivotX; closestY = pivotY; }
        else if (t > 1) { closestX = tipX; closestY = tipY; }
        else { closestX = pivotX + t * dx; closestY = pivotY + t * dy; }

        const distToFlipper = Math.sqrt((ball.x - closestX)**2 + (ball.y - closestY)**2);

        if (distToFlipper < ball.radius + flipper.height / 2 && flipper.active) {
          // Rebond puissant
          const flipperNormalAngle = flipper.angle + (isLeft ? Math.PI/2 : -Math.PI/2); // Normale au flipper
          ball.vx = Math.cos(flipperNormalAngle) * 10; // Force de frappe
          ball.vy = Math.sin(flipperNormalAngle) * 10 - 5; // Avec un peu de montée
          setScore(s => s + 10); // Points pour un contact avec flipper actif
        }
      });
    }

    // Mouvement des flippers
    const updateFlipper = (flipper) => {
      if (flipper.active && flipper.angle > FLIPPER_MAX_ANGLE * (rightFlipperRef.current === flipper ? -1 : 1)) {
        flipper.angle -= FLIPPER_SPEED * (rightFlipperRef.current === flipper ? -1 : 1);
      } else if (!flipper.active && flipper.angle < 0 * (rightFlipperRef.current === flipper ? -1 : 1)) {
        flipper.angle += FLIPPER_SPEED * (rightFlipperRef.current === flipper ? -1 : 1);
      }
      flipper.angle = Math.max(FLIPPER_MAX_ANGLE, Math.min(0, flipper.angle)); // Flipper Gauche
      if (rightFlipperRef.current === flipper) {
        flipper.angle = Math.min(-FLIPPER_MAX_ANGLE, Math.max(0, flipper.angle)); // Flipper Droit
      }
    };
    updateFlipper(leftFlipperRef.current);
    updateFlipper(rightFlipperRef.current);


    // Dessiner le jeu
    const ctx = canvasRef.current.getContext('2d');
    draw(ctx);

    requestRef.current = requestAnimationFrame(updateGame);
  }, [isPlaying, isGameOver, balls, draw]); // Ajout de 'balls' et 'draw'

  useEffect(() => {
    requestRef.current = requestAnimationFrame(updateGame);
    return () => cancelAnimationFrame(requestRef.current);
  }, [updateGame]);


  // --- CONTRÔLES ---
  const handleGameKeyDown = useCallback((e) => {
    if (e.key === 'z' || e.key === 'ArrowLeft') leftFlipperRef.current.active = true;
    if (e.key === 'm' || e.key === 'ArrowRight') rightFlipperRef.current.active = true;
    if (e.key === ' ') {
      e.preventDefault();
      if (isGameOver) {
        resetGame();
      } else if (!ballRef.current.launched && !plungerRef.current.pulling) {
        plungerRef.current.pulling = true;
        setIsPlaying(true); // Met le jeu en mode actif
        setGameMessage('Tirez le lanceur !');
      } else if(plungerRef.current.pulling) {
        // Relâcher le lanceur
        ballRef.current.launched = true;
        ballRef.current.vy = -plungerRef.current.pullBack / 2; // Force proportionnelle
        plungerRef.current.pulling = false;
        plungerRef.current.pullBack = 0;
        setGameMessage('');
      } else {
        // Pause/Play si le jeu est lancé
         setIsPlaying(prev => !prev);
         setGameMessage(isPlaying ? 'Pause' : '');
      }
    }
    // Tirer le lanceur
    if (e.key === 'ArrowDown' && plungerRef.current.pulling && !ballRef.current.launched) {
        e.preventDefault();
        plungerRef.current.pullBack = Math.min(plungerRef.current.height - 10, plungerRef.current.pullBack + 5);
    }
  }, [isGameOver, isPlaying]); // Ajout de isPlaying

  const handleGameKeyUp = useCallback((e) => {
    if (e.key === 'z' || e.key === 'ArrowLeft') leftFlipperRef.current.active = false;
    if (e.key === 'm' || e.key === 'ArrowRight') rightFlipperRef.current.active = false;
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleGameKeyDown);
    document.addEventListener('keyup', handleGameKeyUp);
    return () => {
      document.removeEventListener('keydown', handleGameKeyDown);
      document.removeEventListener('keyup', handleGameKeyUp);
    };
  }, [handleGameKeyDown, handleGameKeyUp]);

  const resetGame = () => {
    ballRef.current = { x: TABLE_WIDTH - BALL_RADIUS - 15, y: TABLE_HEIGHT - 50, vx: 0, vy: 0, radius: BALL_RADIUS, launched: false };
    leftFlipperRef.current = { ...leftFlipperRef.current, angle: 0, active: false };
    rightFlipperRef.current = { ...rightFlipperRef.current, angle: 0, active: false };
    plungerRef.current = { ...plungerRef.current, pullBack: 0, pulling: false };
    setScore(0);
    setBalls(3);
    setIsGameOver(false);
    setIsPlaying(false); // Important pour que le message "Appuyez sur ESPACE" s'affiche
    setGameMessage('Appuyez sur ESPACE pour Lancer');
  };

  // Fonctions pour les boutons tactiles
  const activateFlipper = (flipperRef) => { flipperRef.current.active = true; };
  const deactivateFlipper = (flipperRef) => { flipperRef.current.active = false; };
  
  const handlePlungerButton = () => {
    if (isGameOver) {
        resetGame();
        return;
    }
    if (!ballRef.current.launched && !plungerRef.current.pulling) {
        plungerRef.current.pulling = true;
        setIsPlaying(true);
        setGameMessage('Tirez et relâchez !'); // Indiquer à l'utilisateur de tirer et relâcher
        // Simuler une traction maximale et un relâchement immédiat pour le bouton
        setTimeout(() => {
            if(plungerRef.current.pulling) { // Vérifier si toujours en mode traction
                ballRef.current.launched = true;
                ballRef.current.vy = -PLUNGER_STRENGTH_MAX / 2; 
                plungerRef.current.pulling = false;
                plungerRef.current.pullBack = 0;
                setGameMessage('');
            }
        }, 300); // Petit délai pour simuler la traction
    } else if (isPlaying && ballRef.current.launched){
        setIsPlaying(false); // Pause
        setGameMessage('Pause');
    } else if (!isPlaying && ballRef.current.launched) {
        setIsPlaying(true); // Reprendre
        setGameMessage('');
    }
  };


  return (
    <div className="flipper-game-container">
      <div className="flipper-game-ui">
        <div className="score">Score: {score}</div>
        <div className="balls">Billes: {balls}</div>
        <button onClick={resetGame} className="flipper-reset-button" aria-label="Recommencer"><FaRedo /></button>
      </div>
      <canvas ref={canvasRef} width={TABLE_WIDTH} height={TABLE_HEIGHT} />
      <div className="flipper-game-message">{gameMessage}</div>
      <div className="flipper-mobile-controls">
        <button 
            onTouchStart={() => activateFlipper(leftFlipperRef)} 
            onTouchEnd={() => deactivateFlipper(leftFlipperRef)}
            onMouseDown={() => activateFlipper(leftFlipperRef)}
            onMouseUp={() => deactivateFlipper(leftFlipperRef)}
            aria-label="Flipper Gauche"
        >Gauche</button>
        <button onClick={handlePlungerButton} className="plunger-button" aria-label={isGameOver ? "Rejouer" : "Lancer/Pause"}>
            {isGameOver ? <FaRedo /> : (!ballRef.current.launched ? "Lancer" : (isPlaying ? "Pause" : <FaPlay />))}
        </button>
        <button 
            onTouchStart={() => activateFlipper(rightFlipperRef)} 
            onTouchEnd={() => deactivateFlipper(rightFlipperRef)}
            onMouseDown={() => activateFlipper(rightFlipperRef)}
            onMouseUp={() => deactivateFlipper(rightFlipperRef)}
            aria-label="Flipper Droit"
        >Droit</button>
      </div>
    </div>
  );
}

export default FlipperGame;